import {
  clearCachedCurrentToken,
  getCachedCurrentToken,
  setCachedCurrentToken,
} from './cache';
import { fetchToken, Token } from './fetchToken';
import { getLoginUrl } from '../helpers/config';

type HTTPErrorWithAuthenticationUrl = {
  authenticationUrl: string;
};

let latestTokenPromise: Promise<Token> | null = null;

export const clearToken = () => {
  clearCachedCurrentToken();
};

export const getAuthenticationUrl = (res: HTTPErrorWithAuthenticationUrl) =>
  res.authenticationUrl;

// IMPORTANT! change the version here anytime `innerGetCurrentTokenWithoutRedirect` is being updated!
const getTokenFunctionVersion = 1;

const innerGetCurrentTokenWithoutRedirect = () => {
  if (latestTokenPromise) {
    return latestTokenPromise;
  }
  const currentToken = getCachedCurrentToken();
  if (
    !currentToken ||
    new Date(currentToken.expiresAt).getTime() - Date.now() <= 300 * 1000
  ) {
    latestTokenPromise = fetchToken()
      .then((token) => {
        latestTokenPromise = null;
        setCachedCurrentToken(token);
        return token;
      })
      .catch((e) => {
        latestTokenPromise = null;
        if (getAuthenticationUrl(e)) {
          clearToken();
        }
        throw e;
      });
    return latestTokenPromise;
  }
  return Promise.resolve(currentToken);
};

const globalGetTokenFunctionKeyName = '__ACSGetAccessToken';
const globalGetTokenFunctionVersionKeyName = '__ACSGetAccessTokenVersion';

declare global {
  interface Window {
    [globalGetTokenFunctionKeyName]?: () => Promise<Token>;
    [globalGetTokenFunctionVersionKeyName]?: number;
  }
}

export const getCurrentTokenWithoutRedirect = (): Promise<Token> => {
  let fn = window[globalGetTokenFunctionKeyName];
  const fnVersion = window[globalGetTokenFunctionVersionKeyName];
  if (!fn || !fnVersion || fnVersion < getTokenFunctionVersion) {
    fn = window[globalGetTokenFunctionKeyName] =
      innerGetCurrentTokenWithoutRedirect;
    window[globalGetTokenFunctionVersionKeyName] = getTokenFunctionVersion;
  }
  return fn();
};

export const getCurrentToken = () =>
  getCurrentTokenWithoutRedirect().catch((e) => {
    const authenticationUrl = getAuthenticationUrl(e);
    if (authenticationUrl) {
      window.location.assign(authenticationUrl);
    }
    throw e;
  });

export const logout = (redirectUrl: string = window.location.href) => {
  clearToken();
  window.location.assign(
    `${getLoginUrl()}/api/v1/authentication/logout?redirectUrl=${redirectUrl}`,
  );
};

export const convertToDocsToken = ({
  accessToken,
  expiresAt,
}: {
  accessToken: string;
  expiresAt: string;
}) => ({
  token: accessToken,
  token_expiration: Math.round(
    (new Date(expiresAt).getTime() - Date.now()) / 1000,
  ),
});
